import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/stayflow`;

const client = axios.create({ baseURL: BASE, timeout: 20000 });

// unwrap upstream { data: ... }
const unwrap = (res) => (res?.data && "data" in res.data ? res.data.data : res.data);

export const getConfig = () => client.get("/config").then((r) => r.data);
export const ping = () => client.get("/ping").then((r) => r.data);

export const getProperties = () => client.get("/properties").then(unwrap);
export const getProperty = (id) => client.get(`/properties/${id}`).then(unwrap);
export const getRooms = (propertyId) => client.get(`/properties/${propertyId}/rooms`).then(unwrap);
export const getReviews = (propertyId) => client.get(`/properties/${propertyId}/reviews`).then(unwrap);

export const getAvailability = (roomId, start, end) =>
  client.get(`/rooms/${roomId}/availability`, { params: { start, end } }).then(unwrap);

export const getRules = (propertyId) => client.get(`/properties/${propertyId}/rules`).then(unwrap);

// Server-priced quote: GST, fees, the exact amount Razorpay will charge, plus the
// cancellation terms for the chosen check-in date. Never compute any of this locally.
export const getFeeQuote = (params = {}) => {
  const q = {};
  Object.entries(params).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") q[k] = v; });
  return client.get("/fees/quote", { params: q }).then(unwrap);
};

// "Today" is always India time, never the visitor's device clock.
export const istToday = () => {
  const [y, m, d] = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date()).split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

export const validateCoupon = (code, total) =>
  client.get(`/coupons/${encodeURIComponent(code)}`, { params: { total } }).then(unwrap);

// The server verifies ownership — pass the guest's phone and/or email.
export const getBooking = (id, { guest_phone, guest_email } = {}) =>
  client.get(`/bookings/${id}`, {
    params: { ...(guest_phone ? { guest_phone } : {}), ...(guest_email ? { guest_email } : {}) },
  }).then(unwrap);
export const getPaymentStatus = (id) => client.get(`/bookings/${id}/payment-status`).then(unwrap);

// The server sometimes creates the Razorpay link a few seconds after the booking.
// Re-check payment-status every 2s (max 30s) — never creates another booking.
export const waitForPaymentLink = async (id, { timeoutMs = 30000, intervalMs = 2000 } = {}) => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, intervalMs));
    try {
      const s = await getPaymentStatus(id);
      if (s?.payment_link_url) return s.payment_link_url;
      if (s?.status && !["pending_payment", "pending"].includes(s.status)) return null;
    } catch { /* transient — keep checking */ }
  }
  return null;
};
export const getMapPoints = () => client.get("/map").then(unwrap);
export const getPropertyGeo = (id) => client.get(`/properties/${id}/geo`).then(unwrap);

// Haversine distance in km
export const distanceKm = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return Infinity;
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

// Google place search — always through our server, so the key stays hidden.
export const suggestPlaces = (q, session) =>
  client.get("/places/suggest", { params: { q, session } }).then((r) => r.data?.data || { places: [], capped: false });

export const getPlaceDetails = (placeId, session) =>
  client.get("/places/details", { params: { place_id: placeId, session } }).then((r) => r.data?.data || null);

export const getCsrf = () => client.get("/csrf").then((r) => r.data.csrf_token);

// Keep this in step with the backend's _PHONE_RE: optional +, 8–15 digits, so
// foreign numbers with a country code work as well as Indian ones.
export const validPhoneIntl = (v) => {
  const s = (v || "").trim();
  if (!/^\+?[\d\s-]+$/.test(s)) return false;
  const d = s.replace(/\D/g, "");
  return d.length >= 8 && d.length <= 15;
};

export const validEmailAddr = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test((v || "").trim());

// Stable per-browser id so the server can slow down repeated booking attempts
// from one device even when the IP changes.
const DEVICE_KEY = "sf_device_id";
export const deviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random().toString(16).slice(2)}`);
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
};

export const createBooking = async (body) => {
  const token = await getCsrf();
  return client.post("/bookings", body, {
    headers: { "X-CSRF-Token": token, "X-Device-Id": deviceId() },
  }).then(unwrap);
};

export const getCancelQuote = (id) => client.get(`/bookings/${id}/cancel-quote`).then(unwrap);

// opts: { shown_refund, shown_fee } -> sends confirm=true with the figures the guest saw
export const cancelBooking = async (id, opts = null) => {
  const token = await getCsrf();
  const params = opts
    ? { confirm: "true", shown_refund: Number(opts.shown_refund ?? 0).toFixed(2), shown_fee: Number(opts.shown_fee ?? 0).toFixed(2) }
    : undefined;
  return client.put(`/bookings/${id}/cancel`, {}, { headers: { "X-CSRF-Token": token }, params }).then(unwrap);
};

// Map error -> friendly, actionable message (per spec)
export const friendlyError = (err, context = "generic") => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 401) return "Site temporarily unavailable, please try again.";
  if (status === 409) return "Those dates just got booked — please pick new ones.";
  if (status === 403) return "Your session expired. Please refresh and try again.";
  if (status === 429) return typeof detail === "string" && detail ? detail : "Too many attempts. Please wait a minute and try again.";
  if (status === 404 && context === "coupon") return "That coupon code isn't valid.";
  if (status === 410 && context === "coupon") return "That coupon has expired.";
  if (status === 503) return "Bookings aren't live yet. Please check back soon.";
  if (status === 502) return "The booking service is busy right now. Please try again.";
  if (status === 504 || err?.code === "ECONNABORTED") return "The connection timed out. Please try again in a moment.";
  if (typeof detail === "string") return detail;
  return "Something went wrong. Please try again.";
};

// A refused booking shows the App's own message, exactly as it sends it.
export const bookingError = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d.trim();
  if (typeof d?.message === "string" && d.message.trim()) return d.message.trim();
  return friendlyError(err);
};

export const fmtINR = (n) => {  if (n === null || n === undefined || isNaN(n)) return null;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
};

// Breakdown lines — always show paise (₹1,495.24).
export const fmtINR2 = (n) => {
  if (n === null || n === undefined || isNaN(n)) return null;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

// Totals — exactly as the server sends them (₹1,570 stays ₹1,570).
export const fmtAmount = (n) => {
  if (n === null || n === undefined || isNaN(n)) return null;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
};

// Map common IANA timezones -> country, used as a graceful fallback when a
// property has no address/city/state/country filled in the source app.
const TZ_COUNTRY = {
  "Asia/Kolkata": "India",
  "Asia/Calcutta": "India",
  "Asia/Dhaka": "Bangladesh",
  "Asia/Kathmandu": "Nepal",
  "Asia/Colombo": "Sri Lanka",
  "Asia/Dubai": "United Arab Emirates",
  "Asia/Singapore": "Singapore",
  "Asia/Bangkok": "Thailand",
};

export const locationText = (p = {}, full = false) => {
  const primary = full ? (p.address || p.city) : p.city;
  const parts = [primary, p.state].map((x) => (x || "").trim()).filter(Boolean);
  if (full && (p.country || "").trim()) parts.push(p.country.trim());
  if (parts.length) return [...new Set(parts)].join(", ");
  if ((p.country || "").trim()) return p.country.trim();
  return TZ_COUNTRY[p.timezone] || "";
};
